import React, { useState } from 'react';
import GatewayCredentialsForm from './settings/GatewayCredentials';
import RoutingRules from './settings/RoutingRules';
import BillingTaxation from './settings/BillingTaxation';
import RecoverySettings from './settings/RecoverySettings';
import BrandingSettings from './settings/BrandingSettings';
import AnalyticsSettings from './settings/AnalyticsSettings';
import DataManagementSettings from './settings/DataManagementSettings';
import { ActiveSession, AuditLog } from '../types';
import { useSettings } from './SettingsContext';
import { useNotifications } from './NotificationContext';
import { TokenManager } from './api/TokenManager';

const MOCK_SESSIONS: ActiveSession[] = [
  { id: 'SESS-01', device: 'Chrome on macOS (Current)', location: 'San Francisco, US', ip: '192.168.1.1', isCurrent: true, lastActive: 'Active Now' },
  { id: 'SESS-02', device: 'SubScale iOS App', location: 'New York, US', ip: '104.22.11.4', isCurrent: false, lastActive: '12 hours ago' },
];

const MOCK_AUDIT_LOGS: AuditLog[] = [
  { id: '1', adminName: 'Alex Rivera', action: 'Update Subscription Plan', target: 'SUB-101', timestamp: '2024-05-28 10:15', ip: '192.168.1.1' },
  { id: '2', adminName: 'Jordan Smith', action: 'Create Coupon', target: 'SUMMER50', timestamp: '2024-05-27 15:30', ip: '10.0.0.42' },
];

const Settings: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const { addNotification } = useNotifications();
  const [activeTab, setActiveTab] = useState<'gateways' | 'data' | 'analytics' | 'branding' | 'billing' | 'security' | 'logs' | 'recovery'>('data');
  const [showSaved, setShowSaved] = useState(false);

  const handleSave = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-32">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Platform Configuration</h1>
          <p className="text-slate-500 dark:text-slate-400 text-lg font-medium">Global environment variables, datasets, and safety protocols.</p>
        </div>
        <button
          onClick={() => setActiveTab('data')}
          className="flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-900/60 border border-indigo-200 dark:border-indigo-800/60 px-4 py-2 rounded-2xl shadow-sm transition-all cursor-pointer"
          title="Click to manage live or demo data"
        >
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
          <span className="text-[11px] font-black uppercase tracking-widest text-indigo-700 dark:text-indigo-300">Data & Environment</span>
        </button>
      </div>

      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl w-fit flex-wrap">
        {(['data', 'gateways', 'analytics', 'branding', 'billing', 'security', 'logs', 'recovery'] as const).map(tab => (
          <button 
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
          >
            {tab === 'data' ? 'Data Management' : tab}
          </button>
        ))}
      </div>

      <div className="space-y-12 animate-in fade-in duration-500">
        {activeTab === 'data' && <DataManagementSettings onSave={handleSave} />}

        {activeTab === 'gateways' && (
          <div className="space-y-6 animate-in slide-in-from-left-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </div>
                <div>
                  <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Handshake Vault</h2>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">Secure payment processor credentials</p>
                </div>
              </div>
              <GatewayCredentialsForm onSave={handleSave} />
              <RoutingRules onSave={handleSave} />
          </div>
        )}
        
        {activeTab === 'analytics' && <AnalyticsSettings onSave={handleSave} />}

        {activeTab === 'recovery' && (
           <div className="space-y-6 animate-in slide-in-from-left-4">
              <RecoverySettings onSave={handleSave} />
           </div>
        )}

        {activeTab === 'branding' && <BrandingSettings onSave={handleSave} />}

        {activeTab === 'logs' && (
           <div className="space-y-6 animate-in slide-in-from-left-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>
                </div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">System Audit Trail</h2>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-[3rem] border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                 <table className="w-full text-left">
                    <thead>
                       <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700">
                          <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Admin</th>
                          <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Action</th>
                          <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Resource</th>
                          <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Timestamp</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                       {MOCK_AUDIT_LOGS.map(log => (
                         <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-8 py-5 text-sm font-bold text-slate-900 dark:text-white">{log.adminName}</td>
                            <td className="px-8 py-5 text-xs text-slate-600 dark:text-slate-400">{log.action}</td>
                            <td className="px-8 py-5 text-xs font-mono text-indigo-600 font-bold">{log.target}</td>
                            <td className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase">{log.timestamp}</td>
                         </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
           </div>
        )}

        {activeTab === 'billing' && <BillingTaxation onSave={handleSave} />}

        {activeTab === 'security' && (
          <div className="space-y-10 animate-in slide-in-from-left-4">
             <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="p-8 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                   <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Live Sessions</h3>
                   <button className="text-[10px] font-black text-red-600 uppercase tracking-widest">Revoke All</button>
                </div>
                <div className="divide-y divide-slate-50 dark:divide-slate-700">
                   {MOCK_SESSIONS.map(sess => (
                     <div key={sess.id} className="p-8 flex items-center justify-between">
                        <div className="flex items-center gap-6">
                           <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${sess.isCurrent ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></svg>
                           </div>
                           <div>
                              <p className="text-sm font-black text-slate-900 dark:text-white">{sess.device}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{sess.location} • {sess.ip}</p>
                           </div>
                        </div>
                        <p className={`text-[10px] font-black uppercase tracking-widest ${sess.isCurrent ? 'text-emerald-500' : 'text-slate-400'}`}>{sess.lastActive}</p>
                     </div>
                   ))}
                </div>
             </div>

             <TokenManager />
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
